import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import EventForm from '../components/EventForm';
import EventsCalendar from '../components/EventsCalendar';
import { confirmDialog } from '../utils/confirm';

const TYPE_FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'active',    label: 'Active events' },
  { value: 'completed', label: 'Completed' },
  { value: 'accidents', label: 'Accidents' },
];

// Normalizes either ISO ('2026-07-08T...') or legacy ('08.07.2026') dates to
// a 'YYYY-MM-DD' key shared by the calendar dots and the date filter.
const toDateKey = (value) => {
  if (!value) return null;
  const s = String(value);
  const legacy = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (legacy) return `${legacy[3]}-${legacy[2]}-${legacy[1]}`;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DAMAGE_COLORS = { minor: '#f59e0b', major: '#ef4444', total_loss: '#7c3aed' };
const DAMAGE_LABELS = { minor: 'Minor', major: 'Major', total_loss: 'Total Loss' };

const Events = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchQuery, isAdmin } = useAppContext();
  const [events, setEvents] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null); // 'YYYY-MM-DD' | null

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [evtRes, accRes] = await Promise.all([
          apiEndpoints.events.list(),
          apiEndpoints.accidents.list(),
        ]);
        setEvents(evtRes.data);
        setAccidents(accRes.data);

        // Deep-link: open a specific event when navigated here with its id
        // (e.g. clicking an Active Events row on the dashboard).
        const targetId = location.state?.selectedEventId;
        if (targetId) {
          const target = evtRes.data.find((e) => e._id === targetId);
          if (target) setSelectedEvent(target);
          navigate(location.pathname, { replace: true, state: {} });
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    const [evtRes, accRes] = await Promise.all([
      apiEndpoints.events.list(),
      apiEndpoints.accidents.list(),
    ]);
    setEvents(evtRes.data);
    setAccidents(accRes.data);
  };

  const getAccidentsForPlate = (plate) =>
    accidents.filter((a) => a.plate && plate && a.plate.trim() === plate.trim());

  const handleAddEvent = () => { setEditingEvent(null); setShowEventForm(true); };
  const handleEditEvent = (event) => { setEditingEvent(event); setShowEventForm(true); };
  const handleViewDetails = (event) => setSelectedEvent(event);

  const handleSaveEvent = async (payload) => {
    try {
      if (editingEvent) {
        await apiEndpoints.events.update(editingEvent._id, payload);
      } else {
        await apiEndpoints.events.create(payload);
      }
      const updated = await apiEndpoints.events.list();
      setEvents(updated.data);
      if (selectedEvent && editingEvent && selectedEvent._id === editingEvent._id) {
        setSelectedEvent(updated.data.find((e) => e._id === editingEvent._id) || null);
      }
      setShowEventForm(false);
    } catch (err) {
      console.error('Failed to save event:', err);
      alert('Error saving event. Please try again.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!await confirmDialog('Are you sure you want to delete this event?')) return;
    try {
      await apiEndpoints.events.delete(eventId);
      setShowEventForm(false);
      setSelectedEvent(null);
      await refresh();
    } catch (err) {
      console.error('Failed to delete event:', err);
      alert('Error deleting event. Please try again.');
    }
  };

  const handleGoToAccident = (accident) => {
    navigate('/accidents', { state: { selectedAccidentId: accident._id } });
  };

  const handleResolveEvent = async (event) => {
    if (!await confirmDialog('Mark this event as resolved (completed)?')) return;
    try {
      await apiEndpoints.events.update(event._id, { status: 'Completed' });
      const updated = await apiEndpoints.events.list();
      setEvents(updated.data);
      setSelectedEvent(updated.data.find((e) => e._id === event._id) || null);
    } catch (err) {
      console.error('Failed to resolve event:', err);
      alert('Error resolving event. Please try again.');
    }
  };

  const getEventStatus = (event) => {
    const status = (event.status || '').toLowerCase();
    if (status.includes('delay') || status.includes('late') || status.includes('pending') || status.includes('intarziere')) return 'critical';
    if (status.includes('complete') || status.includes('done') || status.includes('finished') || status.includes('finalizat')) return 'good';
    return 'active';
  };

  const getEventLabel = (status) => {
    if (status === 'good') return 'COMPLETED';
    if (status === 'critical') return 'DELAY';
    return 'ACTIVE';
  };

  const getAvatar = (name) => {
    if (!name) return 'EV';
    return name.split(' ').map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase();
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const s = String(value);
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    const dt = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(s);
    return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
  };

  // Calendar dot counts — events keyed by creation date, accidents by their own date.
  const calendarCounts = useMemo(() => {
    const counts = {};
    for (const e of events) {
      const key = toDateKey(e.created);
      if (!key) continue;
      counts[key] = counts[key] || { events: 0, accidents: 0 };
      counts[key].events += 1;
    }
    for (const a of accidents) {
      const key = toDateKey(a.date);
      if (!key) continue;
      counts[key] = counts[key] || { events: 0, accidents: 0 };
      counts[key].accidents += 1;
    }
    return counts;
  }, [events, accidents]);

  if (loading) return <div className="page-loading">Loading events...</div>;

  if (selectedEvent) {
    const status = getEventStatus(selectedEvent);
    const statusLabel = getEventLabel(status);
    const statusColor = status === 'good' ? '#10b981' : status === 'critical' ? '#ef4444' : '#3b82f6';
    const statusBg   = status === 'good' ? '#ecfdf5'  : status === 'critical' ? '#fef2f2'  : '#eff6ff';
    const relatedAccidents = getAccidentsForPlate(selectedEvent.plate);

    return (
      <div>
        <button className="back-btn" onClick={() => setSelectedEvent(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to events
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span className="plate-pill">{selectedEvent.plate || selectedEvent.supplierName || 'General event'}</span>
          </div>
          <div className="vprofile-model">{selectedEvent.evt}</div>
          <div className="vprofile-sub">{[selectedEvent.car, selectedEvent.resp || 'Unassigned'].filter(Boolean).join(' • ')}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Event Details</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {status !== 'good' && (
                      <button className="btn btn-sm" style={{ backgroundColor: '#10b981', color: '#fff', border: 'none' }} onClick={() => handleResolveEvent(selectedEvent)}>Resolve</button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => handleEditEvent(selectedEvent)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteEvent(selectedEvent._id)}>Delete</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                {[
                  ...(selectedEvent.plate ? [['Vehicle plate', selectedEvent.plate]] : []),
                  ...(selectedEvent.car ? [['Car', selectedEvent.car]] : []),
                  ...(selectedEvent.supplierName ? [['Supplier', selectedEvent.supplierName]] : []),
                  ['Event', selectedEvent.evt || '—'],
                  ['Responsible', selectedEvent.resp || 'Unassigned'],
                  ['Created', formatDate(selectedEvent.created)],
                  ['Est. completion', formatDate(selectedEvent.estEnd)],
                  ['Amount spent', selectedEvent.amount != null ? `${selectedEvent.amount.toLocaleString()} ${selectedEvent.currency || 'RON'}` : '—'],
                ].map(([lbl, val]) => (
                  <div key={lbl} className="info-row">
                    <div className="info-lbl">{lbl}</div>
                    <div className="info-val">{val}</div>
                  </div>
                ))}
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <div className="info-lbl">Status</div>
                  <div className="info-val">
                    <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', color: statusColor, backgroundColor: statusBg }}>
                      {statusLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">
                  Accidents
                  {relatedAccidents.length > 0 && (
                    <span style={{ marginLeft: '8px', background: '#fee2e2', color: '#ef4444', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }}>
                      {relatedAccidents.length}
                    </span>
                  )}
                </div>
              </div>
              <div className="card-body" style={{ padding: relatedAccidents.length > 0 ? 0 : undefined }}>
                {relatedAccidents.length === 0 ? (
                  <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>
                    No accidents recorded for <strong>{selectedEvent.plate}</strong>.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '480px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          {['DATE', 'DAMAGE', 'REPAIR COST', 'CLAIM', 'AT FAULT', ''].map((h) => (
                            <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {relatedAccidents.map((acc) => (
                          <tr key={acc._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px 14px' }}>{formatDate(acc.date)}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: DAMAGE_COLORS[acc.damageLevel], backgroundColor: `${DAMAGE_COLORS[acc.damageLevel]}18` }}>
                                {DAMAGE_LABELS[acc.damageLevel] || acc.damageLevel}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                              {acc.repairCost ? `${acc.repairCost.toLocaleString()} ${acc.currency}` : '—'}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: acc.claimStatus === 'open' ? '#3b82f6' : acc.claimStatus === 'closed' ? '#10b981' : acc.claimStatus === 'rejected' ? '#ef4444' : '#9ca3af' }}>
                                {acc.claimStatus || '—'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-3)' }}>
                              {{ driver: 'Our Driver', third_party: 'Third Party', unknown: 'Unknown' }[acc.atFault] || '—'}
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                              <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => handleGoToAccident(acc)}>View →</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="detail-right">
            <div className="card quick-actions-card">
              <div className="card-header"><div className="card-title">Quick Actions</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
                {isAdmin && status !== 'good' && <button className="btn" style={{ width: '100%', backgroundColor: '#10b981', color: '#fff', border: 'none' }} onClick={() => handleResolveEvent(selectedEvent)}>Resolve</button>}
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleEditEvent(selectedEvent)}>Edit Event</button>}
                {isAdmin && <button className="btn btn-danger" style={{ width: '100%' }} onClick={() => handleDeleteEvent(selectedEvent._id)}>Delete Event</button>}
              </div>
            </div>

            {relatedAccidents.length > 0 && (
              <div className="card">
                <div className="card-header"><div className="card-title">Accident Summary</div></div>
                <div className="card-body" style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '36px', fontWeight: 800, color: '#ef4444' }}>{relatedAccidents.length}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-3)', marginBottom: '12px' }}>accident{relatedAccidents.length !== 1 ? 's' : ''} on record</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-1)' }}>
                    {relatedAccidents.reduce((s, a) => s + (a.repairCost || 0), 0).toLocaleString()} RON
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>total repair cost</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <EventForm
          isOpen={showEventForm}
          onClose={() => setShowEventForm(false)}
          event={editingEvent}
          onSave={handleSaveEvent}
        />
      </div>
    );
  }

  const q = searchQuery?.toLowerCase() || '';

  const typeMatches = (event) => {
    if (typeFilter === 'all' || typeFilter === 'accidents') return true;
    const status = getEventStatus(event);
    if (typeFilter === 'active') return status === 'active' || status === 'critical';
    if (typeFilter === 'completed') return status === 'good';
    return true;
  };

  const visibleEvents = events
    .filter((e) => !q || [e.plate, e.car, e.evt, e.resp, e.supplierName].some((v) => v?.toLowerCase().includes(q)))
    .filter(typeMatches)
    .filter((e) => !dateFilter || toDateKey(e.created) === dateFilter);

  const visibleAccidents = accidents
    .filter((a) => !q || [a.plate].some((v) => v?.toLowerCase().includes(q)))
    .filter((a) => !dateFilter || toDateKey(a.date) === dateFilter);

  const showAccidentsTable = typeFilter === 'accidents';

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Events Calendar</div>
          <div className="ph-sub">All active events in the fleet</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleAddEvent}>
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Event
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              style={{
                padding: '6px 14px', borderRadius: 20,
                border: typeFilter === f.value ? '2px solid #1d4ed8' : '1.5px solid #e5e7eb',
                background: typeFilter === f.value ? '#1d4ed8' : '#fff',
                color: typeFilter === f.value ? '#fff' : '#374151',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
          {['list', 'calendar'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none',
                background: viewMode === mode ? '#fff' : 'transparent',
                boxShadow: viewMode === mode ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                color: viewMode === mode ? '#111827' : '#6b7280',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
              }}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'calendar' && (
        <div style={{ marginBottom: 16 }}>
          <EventsCalendar counts={calendarCounts} selectedDate={dateFilter} onSelectDate={setDateFilter} />
        </div>
      )}

      {dateFilter && (
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-3)' }}>
          Showing items for <strong>{new Date(`${dateFilter}T00:00:00`).toLocaleDateString()}</strong>
          {' '}<button onClick={() => setDateFilter(null)} style={{ background: 'none', border: 'none', color: '#0077b6', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Clear</button>
        </div>
      )}

      <div className="card">
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          {showAccidentsTable ? (
            <table className="events-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['VEHICLE', 'DATE', 'DAMAGE', 'REPAIR COST', 'CLAIM', 'AT FAULT', 'ACTION'].map((h) => (
                    <th key={h} style={{ textAlign: h === 'ACTION' ? 'right' : 'left', padding: '12px', color: '#9ca3af', fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleAccidents.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-3)' }}>No accidents found.</td></tr>
                ) : visibleAccidents.map((acc) => (
                  <tr key={acc._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '13px', fontWeight: '500' }}>
                        {acc.plate || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>{formatDate(acc.date)}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: DAMAGE_COLORS[acc.damageLevel], backgroundColor: `${DAMAGE_COLORS[acc.damageLevel]}18` }}>
                        {DAMAGE_LABELS[acc.damageLevel] || acc.damageLevel}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontWeight: 600 }}>{acc.repairCost ? `${acc.repairCost.toLocaleString()} ${acc.currency}` : '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: acc.claimStatus === 'open' ? '#3b82f6' : acc.claimStatus === 'closed' ? '#10b981' : acc.claimStatus === 'rejected' ? '#ef4444' : '#9ca3af' }}>
                        {acc.claimStatus || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{{ driver: 'Our Driver', third_party: 'Third Party', unknown: 'Unknown' }[acc.atFault] || '—'}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => handleGoToAccident(acc)}>View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="events-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['VEHICLE / SUPPLIER', 'EVENT', 'RESPONSIVE', 'CREATION DATE', 'EST. COMPLETION DATE', 'AMOUNT', 'STATUS', 'ACCIDENT', 'ACTION'].map((h) => (
                    <th key={h} style={{ textAlign: h === 'ACTION' ? 'right' : 'left', padding: '12px', color: '#9ca3af', fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleEvents.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-3)' }}>No events found.</td></tr>
                ) : visibleEvents.map((event) => {
                  const status = getEventStatus(event);
                  const statusLabel = getEventLabel(status);
                  const statusColor = status === 'good' ? '#10b981' : status === 'critical' ? '#ef4444' : '#3b82f6';
                  const accidentCount = getAccidentsForPlate(event.plate).length;

                  return (
                    <tr key={event._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px' }}>
                        {event.plate ? (
                          <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '13px', fontWeight: '500' }}>
                            {event.plate}
                          </span>
                        ) : (
                          <span style={{ fontSize: '13px', color: 'var(--text-3)' }}>{event.supplierName || 'General'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        <div>{event.car}</div>
                        <div style={{ color: '#6b7280', fontSize: '13px' }}>{event.evt}</div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#3b82f6', color: 'white', fontSize: '12px', fontWeight: '600' }}>
                            {getAvatar(event.resp)}
                          </span>
                          <span>{event.resp || 'Unassigned'}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>{formatDate(event.created)}</td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>{formatDate(event.estEnd)}</td>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: 600 }}>{event.amount != null ? `${event.amount.toLocaleString()} ${event.currency || 'RON'}` : '—'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', color: statusColor, backgroundColor: status === 'good' ? '#ecfdf5' : status === 'critical' ? '#fef2f2' : '#eff6ff' }}>
                          {statusLabel}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {accidentCount > 0 ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: 700, color: '#ef4444', backgroundColor: '#fee2e2' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            {accidentCount}
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db', fontSize: '13px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-xs" onClick={() => handleViewDetails(event)} style={{ color: '#3b82f6' }}>
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <EventForm
        isOpen={showEventForm}
        onClose={() => setShowEventForm(false)}
        event={editingEvent}
        onSave={handleSaveEvent}
      />
    </div>
  );
};

export default Events;
